// v6 F9.8 — Login screen: korte pitch + Google-knop.
// v6 F10  — Blokkeer Google-login in FB/IG/Messenger/LinkedIn in-app browsers
//           (Google geeft daar "403: disallowed_useragent").
// v6 F11  — Network-error indicatie + redirect-stuck fallback.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Ruler, ExternalLink, AlertTriangle, Copy, Wifi } from "lucide-react";
import { toast } from "sonner";
import { detectInAppBrowser, IN_APP_LABEL } from "../lib/inAppBrowser";
import { describeAuthError } from "../lib/authFetch";

export default function LoginScreen() {
  const { user, loading, login, networkError } = useAuth();
  const [redirecting, setRedirecting] = React.useState(false);
  // v6 F11 — Als de OAuth-redirect na 10s nog niet is gestart (zeldzaam,
  // gebeurt soms op iPhone Safari), tonen we een "Probleem?" fallback.
  const [redirectStuck, setRedirectStuck] = React.useState(false);
  // v6 F10 — Detect in-app browsers (FB/IG/Messenger/LinkedIn). Compute once
  // on mount so the warning persists even na re-renders.
  const inAppBrowser = React.useMemo(() => detectInAppBrowser(), []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleLogin = () => {
    setRedirecting(true);
    setRedirectStuck(false);
    setTimeout(login, 250);
    // Fallback: na 10s nog steeds hier? → toon "stuck" UI
    setTimeout(() => setRedirectStuck(true), 10000);
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link gekopieerd — plak in Safari of Chrome");
    } catch {
      toast.error("Kopiëren mislukt — kopieer de link handmatig");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-5">
      <div
        className="w-full max-w-md bg-white border border-[#E5E2DC] rounded-2xl shadow-sm p-8"
        data-testid="login-screen"
      >
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#D96C4A] flex items-center justify-center text-white">
            <Ruler className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold text-[#2C2C2A] leading-tight">
              EmmaMeasure™
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5D7259] font-bold">
              Powered by EmmaForge™
            </div>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-bold text-[#2C2C2A] leading-snug">
          Slim opmeten.<br />Snel offreren.
        </h1>

        <p className="text-sm text-[#4F4F4D] mt-3 mb-6 leading-relaxed">
          Log in om je klanten, projecten en offertes te beheren. Gemaakt voor
          schilders en decorateurs die op de werf snel een correcte prijs willen
          geven.
        </p>

        {inAppBrowser ? (
          <InAppBrowserBlock browser={inAppBrowser} onCopy={copyUrl} />
        ) : (
          <>
            {networkError && !redirecting && (
              <div
                className="mb-3 rounded-xl border border-[#E5C77A] bg-[#FFF8E4] p-3 text-xs text-[#7B5E14] flex gap-2 items-start"
                data-testid="login-network-error"
                role="status"
              >
                <Wifi className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{describeAuthError(networkError)}</span>
              </div>
            )}
            <Button
              onClick={handleLogin}
              disabled={redirecting}
              className="w-full h-12 rounded-xl bg-[#2C2C2A] hover:bg-[#1A1A18] disabled:opacity-80 text-white font-medium text-base flex items-center justify-center gap-3"
              data-testid="login-google-btn"
            >
              {redirecting ? (
                <>
                  <Spinner /> Doorsturen naar Google…
                </>
              ) : (
                <>
                  <GoogleGlyph />
                  Inloggen met Google
                </>
              )}
            </Button>

            {redirectStuck && redirecting && (
              <div
                className="mt-3 rounded-xl border border-[#E5C77A] bg-[#FFF8E4] p-3 text-xs text-[#7B5E14]"
                data-testid="login-redirect-stuck"
                role="alert"
              >
                <div className="font-semibold mb-1">Doorsturen lukt niet?</div>
                Soms blokkeert je browser de redirect. Probeer een{" "}
                <button
                  onClick={() => window.location.reload()}
                  className="underline font-semibold"
                  data-testid="login-reload-btn"
                >
                  pagina-reload
                </button>{" "}
                of open de app in Safari/Chrome (geen in-app browser).
              </div>
            )}

            <p className="text-[11px] text-[#5D7259] text-center mt-3 leading-snug">
              We gebruiken Google-login om je account veilig te beheren.
              <br />Geen wachtwoorden om te onthouden.
            </p>
          </>
        )}

        <div className="mt-6 pt-4 border-t border-[#F0EFEA]">
          <p className="text-[11px] text-[#5D7259] text-center leading-snug">
            Closed beta — maximum 25 testers. Founding Testers krijgen 50% korting
            op het eerste jaar na officiële lancering.
          </p>
          <p className="mt-3 text-[11px] text-center leading-snug">
            <span className="text-[#7A7A78]">Nog geen toegang?</span>{" "}
            <a
              href="/beta-aanmelden"
              className="text-[#5D7259] font-semibold underline underline-offset-2 hover:text-[#D96C4A] transition-colors"
              data-testid="login-beta-signup-link"
            >
              Vraag beta-toegang aan
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function InAppBrowserBlock({ browser, onCopy }) {
  const label = IN_APP_LABEL[browser] || "deze app";
  return (
    <div
      className="rounded-xl border border-[#E8B86B] bg-[#FFF7E8] p-4"
      data-testid="inapp-browser-warning"
      role="alert"
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-[#B07A1F] flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-heading font-bold text-[#7F4124] leading-snug">
            Google-login werkt niet in {label}
          </div>
          <p className="text-sm text-[#4F4F4D] mt-1 leading-relaxed">
            Google blokkeert inloggen binnen de ingebouwde browser van{" "}
            <b>{label}</b>. Open deze pagina in <b>Safari</b> (iPhone/iPad),{" "}
            <b>Chrome</b> (Android) of een gewone desktop-browser om verder te
            gaan.
          </p>
        </div>
      </div>

      <ol className="text-[12px] text-[#5D7259] list-decimal pl-5 mt-2 space-y-1 leading-snug">
        <li>Tik rechtsboven op het menu (drie puntjes of <i>···</i>)</li>
        <li>Kies <b>"Open in browser"</b> of <b>"Open in Safari / Chrome"</b></li>
        <li>Of kopieer de link en plak ze in een gewone browser:</li>
      </ol>

      <button
        type="button"
        onClick={onCopy}
        className="mt-3 w-full h-11 rounded-xl border border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white font-medium text-sm flex items-center justify-center gap-2 transition-colors"
        data-testid="inapp-copy-link-btn"
      >
        <Copy className="w-4 h-4" /> Link kopiëren
      </button>

      <p className="mt-3 text-[11px] text-[#5D7259] flex items-center gap-1 justify-center">
        <ExternalLink className="w-3 h-3" />
        Eens geopend in Safari of Chrome werkt inloggen meteen.
      </p>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.5-5.2l-6.2-5.3A12 12 0 0 1 12.6 28L6 33.1A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.5l6.2 5.3C42 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
